import { useState, type ReactNode } from "react";
import type { Compendium } from "@lancer-vtt/compendium";
import { type Combatant, type EncounterState, activeActor, disclosureOf } from "@lancer-vtt/domain";
import type { UseEncounter } from "../useEncounter";
import type { ActionDraft } from "../combatInspector";
import type { AutoplayBotControls } from "../autoplayBot";
import { GmDamageRow, RulingPalette } from "../RulingPalette";
import { ControlPanel, type ControlPanelSlot } from "./ControlPanel";
import { AdvisorPanel, AutoplayControls } from "./inspectorPanels";
import { ConcealToggle, DisclosureControl } from "./toggles";

/** One of the GM strip's trays, in the strip's order. */
export type GmTray = "rule" | "damage" | "reveal" | "control" | "more";

const TRAY_LABELS: Record<GmTray, string> = { rule: "RULE", damage: "DAMAGE", reveal: "REVEAL", control: "CONTROL", more: "MORE" };
const TRAY_ORDER: readonly GmTray[] = ["rule", "damage", "reveal", "control", "more"];

/**
 * Which tray each window has open, by window key. Module memory, never a store and never
 * persisted: it lives exactly as long as the page, the same lifetime as a dynamic inspector's
 * geometry (`decisions/0090`), so closing a unit's sheet and reopening it finds the tray the GM
 * left open. A window with no entry starts with no tray open.
 */
const openTrays = new Map<string, GmTray | null>();

/** The open tray for `windowKey`, remembered across the window's close and reopen. */
function useRememberedTray(windowKey: string): [GmTray | null, (t: GmTray | null) => void] {
  const [open, setOpen] = useState<GmTray | null>(() => openTrays.get(windowKey) ?? null);
  return [
    open,
    (t) => {
      openTrays.set(windowKey, t);
      setOpen(t);
    },
  ];
}

/**
 * The unit sheet's **GM strip** (`unit-sheet-redesign` 6.1): one dark row at the sheet's foot with
 * a button per tray and a summary, and the open tray under it. A button opens its tray; pressing
 * it again closes it. One tray is open at a time, remembered per window (see {@link openTrays}),
 * so a GM who rules often keeps RULE open and a preset still fires in two clicks.
 *
 * A tray is part of its window, not a popover: it registers no Esc entry, so Esc closes the window
 * (`decisions/0189`). The strip sits after the sheet's body in the DOM, so tabbing from the header
 * reaches it in reading order. Only the GM's sheet mounts it; players never see it.
 */
export function GmStrip({ windowKey, summary, trays }: { windowKey: string; summary: string; trays: Partial<Record<GmTray, ReactNode>> }) {
  const [open, setOpen] = useRememberedTray(windowKey);
  const shown = TRAY_ORDER.filter((t) => trays[t] !== undefined);
  const openTray = open !== null && trays[open] !== undefined ? open : null;
  return (
    <section className="unit-sheet-gm" aria-label="GM tools">
      <div className="unit-sheet-gmbar">
        <span className="unit-sheet-gm-label">GM</span>
        {shown.map((t) => (
          <button key={t} type="button" className={`unit-sheet-gm-btn${openTray === t ? " is-on" : ""}`} aria-pressed={openTray === t} onClick={() => setOpen(openTray === t ? null : t)}>
            {TRAY_LABELS[t]}
          </button>
        ))}
        <span className="unit-sheet-gm-sum">{summary}</span>
      </div>
      {openTray && (
        <div className="unit-sheet-tray" data-tray={openTray} role="region" aria-label={`${TRAY_LABELS[openTray]} tray`}>
          {trays[openTray]}
        </div>
      )}
    </section>
  );
}

/**
 * The GM strip for a unit sheet, with its five trays:
 * - **RULE**: {@link RulingPalette}.
 * - **DAMAGE**: {@link GmDamageRow}.
 * - **REVEAL**: an NPC's {@link DisclosureControl} and the AS PLAYERS SEE IT toggle, and every
 *   unit's {@link ConcealToggle}.
 * - **CONTROL**: {@link ControlPanel}, the owner and the grants.
 * - **MORE**: {@link AdvisorPanel} on the unit's turn, and {@link AutoplayControls}.
 *
 * The summary names the owner and, for an NPC, what players are shown: `OWNER GM · CLASS`.
 */
export function UnitGmStrip({
  combatant: c,
  state,
  actorId,
  encounter,
  slots,
  compendium,
  setAim,
  bot,
  preview,
  onPreview,
}: {
  /** The GM's view of the unit: never masked. */
  combatant: Combatant;
  state: EncounterState;
  actorId: string;
  encounter: UseEncounter;
  slots: ControlPanelSlot[];
  compendium: Compendium;
  setAim: (a: ActionDraft | null) => void;
  bot: AutoplayBotControls;
  /** Whether AS PLAYERS SEE IT is on. An NPC's only. */
  preview: boolean;
  onPreview: (on: boolean) => void;
}) {
  const isNpc = c.kind === "npc";
  const isActiveActor = activeActor(state.activation) === c.id;
  const owner = c.controllerSlotId ? (slots.find((s) => s.id === c.controllerSlotId)?.name ?? c.controllerSlotId) : "GM";
  const summary = [`OWNER ${owner.toUpperCase()}`, ...(isNpc ? [disclosureOf(c).toUpperCase()] : [])].join(" · ");
  return (
    <GmStrip
      windowKey={c.id}
      summary={summary}
      trays={{
        rule: <RulingPalette combatant={c} state={state} actorId={actorId} dispatch={encounter.dispatch} />,
        damage: <GmDamageRow combatant={c} sendIntent={encounter.sendIntent} />,
        reveal: (
          <div className="unit-sheet-reveal-tray">
            {isNpc && <DisclosureControl combatant={c} actorId={actorId} dispatch={encounter.dispatch} />}
            <ConcealToggle combatant={c} actorId={actorId} dispatch={encounter.dispatch} />
            {isNpc && (
              <button type="button" className={`unit-sheet-preview-btn${preview ? " is-on" : ""}`} aria-pressed={preview} onClick={() => onPreview(!preview)}>
                AS PLAYERS SEE IT
              </button>
            )}
          </div>
        ),
        control: <ControlPanel combatant={c} actorId={actorId} slots={slots} dispatch={encounter.dispatch} />,
        more: (
          <div className="unit-sheet-more-tray">
            {isActiveActor && <AdvisorPanel combatantId={c.id} state={state} compendium={compendium} setAim={setAim} />}
            <AutoplayControls bot={bot} unitId={c.id} unitLabel={c.name} side={c.base.allegiance} isActiveActor={isActiveActor} />
          </div>
        ),
      }}
    />
  );
}
